import { z } from 'zod';
import { SsoOpenIdConnectionResult, useRepositoryMutation } from '@mews-commander/data-access';
import { createExperimentalZodFormResolver, LocalizedText, UseFormControlsParams } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Stack } from '@optimus-web/core';
import {
    ConfigureSsoPanelFieldNames,
    ConfigureSsoPanelFormData,
    ConfigureSsoPanelTokenFields,
    parseFormDataToUserNotification,
    parseSsoConfigToForm,
    ssoSecretValidationSchema,
} from '../../ConfigureSsoPanel';
import { SsoEditPanel } from './SsoEditPanel';

export interface SsoEditTokenProps {
    data: SsoOpenIdConnectionResult;
    active: boolean;
    onClose: () => void;
}

export function SsoEditToken({ data, ...dialogProps }: SsoEditTokenProps) {
    const { onSubmit } = useEditTokenForm();

    return (
        <SsoEditPanel
            title={<LocalizedText textKey={TextKey.SsoDetailsChangeSecretTitle} />}
            id="edit-sso-secret"
            defaultValues={parseSsoConfigToForm(data)}
            resolver={createExperimentalZodFormResolver(editSsoTokenSchema)}
            onSubmit={onSubmit}
            {...dialogProps}
        >
            <Stack spacing="300" vertical>
                <ConfigureSsoPanelTokenFields secretRequired={false} />
            </Stack>
        </SsoEditPanel>
    );
}

const editSsoTokenSchema = z.object({
    ...ssoSecretValidationSchema,
    [ConfigureSsoPanelFieldNames.ClientSecret]: z.string(),
});

type EditSsoTokenFormData = Pick<ConfigureSsoPanelFormData, 'expirationDate' | 'clientSecret' | 'userNotifications'>;

export function useEditTokenForm(): UseFormControlsParams<EditSsoTokenFormData> {
    const { mutateAsync } = useRepositoryMutation('ssoConnection', 'updateOpenIdClientSecret', {
        invalidateQueries: { ssoConnection: ['getOpenId'] },
        meta: {
            disableDefaultErrorHandler: true,
        },
    });

    return {
        onSubmit: async ({ expirationDate, clientSecret, userNotifications }: EditSsoTokenFormData) => {
            await mutateAsync({
                expirationDate,
                clientSecret: clientSecret === '' ? null : clientSecret,
                userNotifications: userNotifications.map(parseFormDataToUserNotification),
            });
        },
    };
}
